// apps/web/src/pages/LandingPage.tsx

import React from 'react';
import { Button } from '@/components/ui/button';
import {
    ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import AnimatedLogisticsScene from '@/components/AnimatedLogisticsScene';
import GradientBackground from '@/components/ui/GradientBackground';
import RoleSwitcher from '@/components/landing/RoleSwitcher';
import HowItWorksTimeline from '@/components/landing/HowItWorksTimeline';
import SmoothScroll from '@/components/ui/SmoothScroll';
import HorizontalScrollSection from '@/components/landing/HorizontalScrollSection';
import TextReveal from '@/components/ui/TextReveal';
import SlotMachineText from '@/components/ui/SlotMachineText';
import ParallaxImage from '@/components/ui/ParallaxImage';
import MarqueeTestimonials from '@/components/landing/MarqueeTestimonials';
import TechFeatures from '@/components/landing/TechFeatures';
import GlobalNetwork from '@/components/landing/GlobalNetwork';
import PartnerMarquee from '@/components/landing/PartnerMarquee';
import { motion, useScroll, useTransform } from 'framer-motion';

const LandingPage = () => {
    // Parallax logic for Hero Text
    const { scrollY } = useScroll();
    const y1 = useTransform(scrollY, [0, 500], [0, 200]);

    return (
        <SmoothScroll>
            <div className="bg-background min-h-screen selection:bg-primary selection:text-white overflow-x-hidden">
                <GradientBackground />

                {/* HERO SECTION */}
                <section className="relative h-screen w-full flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 z-0 opacity-40 md:opacity-100 transition-opacity">
                        <AnimatedLogisticsScene />
                    </div>

                    <div className="container relative z-10 px-4 pt-20">
                        <div className="max-w-4xl mx-auto space-y-8">
                            <motion.div
                                style={{ y: y1 }}
                                className="overflow-hidden"
                            >
                                <div className="flex flex-col items-center w-full">
                                    <SlotMachineText
                                        text="LOGISTICS"
                                        className="text-6xl md:text-9xl font-black tracking-tighter text-foreground leading-[0.9] text-center mix-blend-difference"
                                    />
                                    <SlotMachineText
                                        text="REIMAGINED."
                                        delay={0.5}
                                        className="text-6xl md:text-9xl font-black tracking-tighter text-foreground leading-[0.9] text-center mix-blend-difference"
                                    />
                                </div>
                            </motion.div>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                                className="text-xl md:text-2xl text-muted-foreground text-center max-w-2xl mx-auto leading-relaxed font-light"
                            >
                                The decentralized platform connecting clients, transporters, and fleet operations with neural-network precision.
                            </motion.p>

                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.8 }}
                                className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8"
                            >
                                <Button asChild size="lg" className="rounded-full text-lg h-14 px-8 bg-primary hover:bg-primary/90 shadow-[0_0_40px_-10px_rgba(59,130,246,0.5)] transition-all hover:scale-105">
                                    <Link to="/register">
                                        Get Started <ArrowRight className="ml-2 h-5 w-5" />
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="lg" className="rounded-full text-lg h-14 px-8 border-primary/20 hover:bg-primary/5 backdrop-blur-sm">
                                    <Link to="/how-it-works">
                                        See How It Works
                                    </Link>
                                </Button>
                            </motion.div>
                        </div>
                    </div>


                </section>



                {/* GLOBAL NETWORK SECTION (New) */}
                <GlobalNetwork />

                {/* HORIZONTAL SCROLL (Timeline) */}
                <HorizontalScrollSection />

                {/* TECH FEATURES (New) */}
                <TechFeatures />

                {/* PARALLAX IMAGE SECTION */}
                <section className="py-32 px-4">
                    <div className="container mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                            <div>
                                <h2 className="text-4xl md:text-6xl font-bold tracking-tighter mb-8 leading-none">
                                    Complete Fleet <br />
                                    <span className="text-primary">Ecosystem.</span>
                                </h2>
                                <p className="text-lg text-muted-foreground mb-8">
                                    Whether you're an independent transporter or managing a fleet of vehicles, our platform provides the tools you need. Track vehicles in real-time, manage drivers, assign jobs, and automate operations with military-grade precision.
                                </p>
                                <div className="space-y-4">
                                    {[
                                        "Real-time GPS Tracking",
                                        "Fleet Manager Dashboard",
                                        "Driver Assignment & Dispatch",
                                        "Automated Location Tracking",
                                        "Centralized Payment Management",
                                        "Performance Analytics"
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <div className="h-2 w-2 rounded-full bg-primary" />
                                            <span className="text-lg font-medium">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="h-[600px] w-full rounded-3xl overflow-hidden shadow-2xl relative">
                                <ParallaxImage
                                    src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2070&auto=format&fit=crop"
                                    alt="Fleet Dashboard"
                                    speed={150}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
                                <div className="absolute bottom-8 left-8 right-8 text-white">
                                    <div className="inline-block px-3 py-1 bg-primary/20 text-primary border border-primary/20 rounded-full text-xs font-mono mb-2">LIVE FEED</div>
                                    <p className="font-mono text-sm opacity-80">LAT: 15.768 | LONG: 35.001</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ROLE SWITCHER */}
                <section className="py-20 relative">
                    <RoleSwitcher />
                </section>

                {/* TIMELINE */}
                <HowItWorksTimeline />

                {/* STATS SECTION */}
                <section className="py-32 bg-primary text-primary-foreground relative overflow-hidden">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
                    <div className="container mx-auto px-4 relative z-10">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                            {[
                                { label: "Active Carriers", value: "300+" },
                                { label: "Deliveries", value: "1.2k+" },
                                { label: "Clients", value: "850+" },
                                { label: "Satisfaction", value: "99%" }
                            ].map((stat, i) => (
                                <div key={i} className="space-y-2">
                                    <h3 className="text-5xl md:text-7xl font-black tracking-tighter">{stat.value}</h3>
                                    <p className="text-lg opacity-80 font-medium uppercase tracking-widest">{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA SECTION */}
                <section className="py-32 bg-background relative overflow-hidden">
                    <div className="container mx-auto px-4 text-center">
                        <div className="max-w-3xl mx-auto space-y-8">
                            <h2 className="text-5xl md:text-7xl font-black tracking-tighter">
                                Ready to Move?
                            </h2>
                            <p className="text-xl text-muted-foreground">
                                Join the platform that is redefining logistics in Malawi. Whether you're shipping cargo, transporting goods, or managing a fleet.
                            </p>
                            <div className="flex justify-center gap-4">
                                <Button size="xl" className="rounded-full text-xl px-12 py-8 shadow-2xl shadow-primary/20 hover:shadow-primary/40 transition-shadow">
                                    <Link to="/register">Create Free Account</Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Marquee Testimonials */}
                <MarqueeTestimonials />

                {/* TRUSTED PARTNERS */}
                {/*<PartnerMarquee />*/}


            </div>
        </SmoothScroll>
    );
};

export default LandingPage;